import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { feedbackSendError, sendFeedbackReport } from "@/lib/in-app-feedback";
import { getVisitScope } from "@/lib/visit-queries";
import { useFeedbackStore } from "@/store/feedback.store";
import { FeedbackDialog } from "./FeedbackDialog";

// Mounts the "Send feedback" dialog once per layout and sends reports to
// POST /api/v2/feedback/reports.
export const FeedbackHost = (): JSX.Element => {
  const { t } = useTranslation();
  const open = useFeedbackStore((state) => state.open);
  const visitPublicId = useFeedbackStore((state) => state.visitPublicId);
  const initialType = useFeedbackStore((state) => state.initialType);
  const hide = useFeedbackStore((state) => state.hide);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <FeedbackDialog
      describeError={feedbackSendError}
      initialType={initialType}
      open={open}
      visitPublicId={visitPublicId}
      onOpenChange={(next) => {
        if (!next) hide();
      }}
      onSubmit={async (draft) => {
        const scope = getVisitScope();
        if (!scope) throw Error("Authentication required");
        const dialog = useFeedbackStore.getState();
        const current = () =>
          mounted.current &&
          getVisitScope() === scope &&
          useFeedbackStore.getState() === dialog;
        try {
          await sendFeedbackReport(draft, scope.uid);
        } catch (error) {
          if (current()) throw error;
          return;
        }
        if (!current()) return;
        hide();
        toast({ description: t("feedback.inApp.sent") });
      }}
    />
  );
};
